import React, { useState, useEffect } from "react";
import { useSSEToken } from "../hooks/useSSEToken";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../hooks/useWebSocket";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { subDays, format } from "date-fns";

export const HomeUser = () => {
  const { user, updateUserToken } = useAuth();
  const [newToken, setNewToken] = useState(null);
  const [activeTab, setActiveTab] = useState("medical");
  const [nokUsers, setNokUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: "",
    phone: "",
    importanceLevel: "Medium",
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [updatedNames, setUpdatedNames] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [updatedArray, setUpdatedArray] = useState(null);
  const {
    socketEvent,
    socketEventHealth,
    socketEventSettings,
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  } = useWebSocket(user?.userId);

  const { connected } = useSSEToken(user?.userId, (newTokenReceived) => {
    setNewToken(newTokenReceived);
    updateUserToken(newTokenReceived); // 🔥 Actualizar en contexto de Auth automáticamente
  });

  const [healthData, setHealthData] = useState([
    {
      imageSrc: "/img/oxygen.png",
      value: "--%",
      label: "Oxygen Saturation",
      time: "--:--:--",
    },
    {
      imageSrc: "/img/rate.png",
      value: "--",
      label: "Heart Rate",
      time: "--:--:--",
    },
    {
      imageSrc: "/img/temperature.png",
      value: "--",
      label: "Temperature",
      time: "--:--:--",
    },
    {
      imageSrc: "/img/bloodpressure.png",
      value: "-- / --",
      label: "Blood Pressure",
      time: "--:--:--",
    },
  ]);

  const generateFakeData = () => {
    const fakeData = {
      heartRate: [],
      bloodPressure: [],
      temperature: [],
      oxygenSaturation: [],
    };
    for (let i = 0; i < 14; i++) {
      const date = format(subDays(new Date(), i), "MMM d");

      fakeData.heartRate.push({
        time: date,
        value: Math.floor(Math.random() * (100 - 60) + 60), // 60-100 BPM
      });

      fakeData.bloodPressure.push({
        time: date,
        bpHigh: Math.floor(Math.random() * (140 - 110) + 110), // 110-140
        bpLow: Math.floor(Math.random() * (90 - 70) + 70), // 70-90
      });

      fakeData.temperature.push({
        time: date,
        value: (Math.random() * (37.5 - 36) + 36).toFixed(1), // 36-37.5°C
      });

      fakeData.oxygenSaturation.push({
        time: date,
        value: Math.floor(Math.random() * (100 - 92) + 92), // 92%-100%
      });
    }

    return {
      heartRate: fakeData.heartRate.reverse(),
      bloodPressure: fakeData.bloodPressure.reverse(),
      temperature: fakeData.temperature.reverse(),
      oxygenSaturation: fakeData.oxygenSaturation.reverse(),
    };
  };

  const [historicalData, setHistoricalData] = useState(generateFakeData());

  useEffect(() => {
    if (socketEvent) console.log("📡 Evento WebSocket Recibido:", socketEvent);
    if (socketEventHealth) console.log("❤️ Datos de Salud:", socketEventHealth);
    if (socketEventSettings)
      console.log("⚙️ Configuraciones:", socketEventSettings);
    if (socketEventBP) console.log("🩸 Presión Arterial:", socketEventBP);
    if (socketEventPulse) console.log("💓 Pulso:", socketEventPulse);
    if (socketEventSPO2H) console.log("🫁 SPO2H:", socketEventSPO2H);
    if (socketEventTemperature)
      console.log("🌡 Temperatura:", socketEventTemperature);
  }, [
    socketEvent,
    socketEventHealth,
    socketEventSettings,
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  ]);

  useEffect(() => {
    if (user?.token) {
      fetchUserInfo();
    }
  }, [user]);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    setHealthData((prev) => [
      {
        ...prev[0],
        value: socketEventSPO2H?.spo2hValue
          ? `${socketEventSPO2H.spo2hValue}%`
          : "--%",
        time: socketEventSPO2H?.timestampSeconds
          ? new Date(
              socketEventSPO2H.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[1],
        value: socketEventPulse?.pulse ? `${socketEventPulse.pulse}` : "--",
        time: socketEventPulse?.timestampSeconds
          ? new Date(
              socketEventPulse.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[2],
        value: socketEventTemperature?.temperature
          ? `${(socketEventTemperature.temperature / 10).toFixed(1)}°C` // ✅ Corrección aquí
          : "--",
        time: socketEventTemperature?.timestampSeconds
          ? new Date(
              socketEventTemperature.timestampSeconds * 1000
            ).toLocaleTimeString()
          : "--:--:--",
      },
      {
        ...prev[3],
        value:
          socketEventBP?.bpHigh && socketEventBP?.bpLow
            ? `${socketEventBP.bpHigh} / ${socketEventBP.bpLow}`
            : "-- / --",
        time: socketEventBP?.timestampSeconds
          ? new Date(socketEventBP.timestampSeconds * 1000).toLocaleTimeString()
          : "--:--:--",
      },
    ]);
  }, [
    socketEventBP,
    socketEventPulse,
    socketEventSPO2H,
    socketEventTemperature,
  ]);

  const convertUserArrayToObject = (userInfoArray) => {
    return {
      isRealTimeMode: userInfoArray[0],
      isMonitoringActive: userInfoArray[1],
      maxStoragePeriod: userInfoArray[2],
      maxStorageAbnormalPeriod: userInfoArray[3],

      bodyGender: userInfoArray[4],
      bodyYearOfBirth: userInfoArray[5],
      bodyHeight: userInfoArray[6],
      bodyWeight: userInfoArray[7],
      bodySkinLevel: userInfoArray[8],

      fetchIntervalPulse: userInfoArray[9],
      fetchIntervalBp: userInfoArray[10],
      fetchIntervalTemperature: userInfoArray[11],
      fetchIntervalSpo2h: userInfoArray[12],

      criticalLevelPulseMin: userInfoArray[13],
      criticalLevelPulseMax: userInfoArray[14],
      criticalLevelBpHighMin: userInfoArray[15],
      criticalLevelBpHighMax: userInfoArray[16],
      criticalLevelBpLowMin: userInfoArray[17],
      criticalLevelBpLowMax: userInfoArray[18],
      criticalLevelTemperatureMin: userInfoArray[19],
      criticalLevelTemperatureMax: userInfoArray[20],
      criticalLevelSpo2hMin: userInfoArray[21],
      criticalLevelSpo2hMax: userInfoArray[22],

      delayEmergencyCall: userInfoArray[23],
      alarmStage: userInfoArray[24],

      token: userInfoArray[25],

      template1: userInfoArray[26],
      template2: userInfoArray[27],
      template3: userInfoArray[28],
      template4: userInfoArray[29],
      template5: userInfoArray[30],
      template6: userInfoArray[31],
      template7: userInfoArray[32],
      template8: userInfoArray[33],
      template9: userInfoArray[34],
      template10: userInfoArray[35],
      template11: userInfoArray[36],
      template12: userInfoArray[37],
      template13: userInfoArray[38],
      template14: userInfoArray[39],
      template15: userInfoArray[40],
      template16: userInfoArray[41],
    };
  };
  const fetchUserInfo = async () => {
    try {
      const response = await fetch(
        "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/userInfSettingsAndState",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const result = await response.json();
      console.log("📢 Datos crudos de la API:", result);

      if (result.win === 1 && result.data?.[1]?.length > 0) {
        let userInfoArray;

        try {
          userInfoArray = JSON.parse(result.data[1][0]);
        } catch (error) {
          console.error("❌ Error al parsear JSON:", error);
          return;
        }

        let userInfoObject = convertUserArrayToObject(userInfoArray);

        userInfoObject.token = user.token;

        setUserInfo(userInfoObject);

        console.log(
          "📢 Usuario convertido en objeto con nuevo token:",
          userInfoObject
        );

        const convertedArray = convertObjectToArray(userInfoObject);
        setUpdatedArray(convertedArray);

        console.log("📢 Objeto convertido nuevamente a array:", convertedArray);
      } else {
        console.warn("⚠ No se encontraron datos válidos en la API.");
      }
    } catch (error) {
      console.error("❌ Error al obtener la información del usuario:", error);
    }
  };
  const convertObjectToArray = (userInfoObject) => {
    return [
      userInfoObject.isRealTimeMode,
      userInfoObject.isMonitoringActive,
      userInfoObject.maxStoragePeriod,
      userInfoObject.maxStorageAbnormalPeriod,
      userInfoObject.bodyGender,
      userInfoObject.bodyYearOfBirth,
      userInfoObject.bodyHeight,
      userInfoObject.bodyWeight,
      userInfoObject.bodySkinLevel,
      userInfoObject.fetchIntervalPulse,
      userInfoObject.fetchIntervalBp,
      userInfoObject.fetchIntervalTemperature,
      userInfoObject.fetchIntervalSpo2h,
      userInfoObject.criticalLevelPulseMin,
      userInfoObject.criticalLevelPulseMax,
      userInfoObject.criticalLevelBpHighMin,
      userInfoObject.criticalLevelBpHighMax,
      userInfoObject.criticalLevelBpLowMin,
      userInfoObject.criticalLevelBpLowMax,
      userInfoObject.criticalLevelTemperatureMin,
      userInfoObject.criticalLevelTemperatureMax,
      userInfoObject.criticalLevelSpo2hMin,
      userInfoObject.criticalLevelSpo2hMax,
      userInfoObject.delayEmergencyCall,
      userInfoObject.alarmStage,
      userInfoObject.token,
      userInfoObject.template1,
      userInfoObject.template2,
      userInfoObject.template3,
      userInfoObject.template4,
      userInfoObject.template5,
      userInfoObject.template6,
      userInfoObject.template7,
      userInfoObject.template8,
      userInfoObject.template9,
      userInfoObject.template10,
      userInfoObject.template11,
      userInfoObject.template12,
      userInfoObject.template13,
      userInfoObject.template14,
      userInfoObject.template15,
      userInfoObject.template16,
    ];
  };

  useEffect(() => {
    if (userInfo) {
      const updatedUserInfo = { ...userInfo, token: user.token };

      const updatedArray = Object.values(updatedUserInfo);

      const settingsString = JSON.stringify(updatedArray);

      const sendUpdatedSettings = async () => {
        try {
          const response = await fetch(
            "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/addSettingsAndState",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                auth_token: user.token,
              },
              body: JSON.stringify({ settingsAndState: settingsString }),
            }
          );

          const result = await response.json();
          console.log("📢 Respuesta de la API:", result);

          if (response.ok) {
            console.log("✅ Datos enviados automáticamente.");
          } else {
            console.error("❌ Error al enviar los datos:", result);
          }
        } catch (error) {
          console.error("❌ Error en la petición:", error);
        }
      };

      sendUpdatedSettings();
    }
  }, [userInfo]);

  useEffect(() => {
    if (user?.userId) fetchContacts();
  }, [user]);

  const fetchContacts = async () => {
    if (!user?.token) {
      console.error("❌ No token found. User must be logged in.");
      return;
    }

    try {
      const response = await fetch(
        "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/listNoksForUser",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const result = await response.json();
      if (result.win === 1 && result.data) {
        setNokUsers(result.data);
        console.log("📢 Datos obtenidos de la API:", result.data);
      } else {
        setNokUsers([]);
      }
    } catch (error) {
      console.error("❌ Error fetching contacts:", error);
    }
  };

  const handleInputChange = (e) => {
    setNewUser({ ...newUser, [e.target.name]: e.target.value });
  };

  const handleAddNok = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!newUser.name || !newUser.phone || !newUser.importanceLevel) {
      setErrorMessage("❌ All fields are required.");
      setLoading(false);
      return;
    }

    try {
      const addContactResponse = await fetch(
        "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/addContact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
          body: JSON.stringify({
            userId: user?.userId,
            PhoneNumberContact: newUser.phone,
            NameContact: newUser.name,
            LevelContact: newUser.importanceLevel,
          }),
        }
      );

      if (!addContactResponse.ok) {
        throw new Error("❌ Failed to add contact.");
      }

      console.log("✅ Contact added successfully.");

      const sendNewContactResponse = await fetch(
        "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/sendNewContact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
          body: JSON.stringify({
            phoneNumber: newUser.phone,
            userId: user?.userId,
          }),
        }
      );

      if (!sendNewContactResponse.ok) {
        throw new Error("❌ Failed to send new contact notification.");
      }

      console.log("✅ New contact notification sent successfully.");

      setNewUser({ name: "", phone: "", importanceLevel: "Medium" });
      fetchContacts();
    } catch (error) {
      console.error("❌ Error adding/sending contact:", error);
      setErrorMessage(error.message);
    }

    setLoading(false);
  };

  const fetchTemperatureData = async () => {
    try {
      const response = await fetch(
        "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/userInfTemperature",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
          body: JSON.stringify({
            userId: user.userId,
            timeStamp: 1,
          }),
        }
      );

      const result = await response.json();
      console.log("📊 Respuesta cruda de temperatura:", result);

      const rawData = result?.data?.[2];
      if (!Array.isArray(rawData)) {
        console.warn("⚠ La respuesta de temperatura no es un array válido.");
        return;
      }

      // Agrupar por día (YYYY-MM-DD => [temperaturas])
      const groupedByDay = rawData.reduce((acc, [timestamp, value]) => {
        if (!timestamp || !value) return acc;
        const date = new Date(timestamp * 1000);
        if (isNaN(date.getTime())) return acc;

        const dayKey = date.toISOString().split("T")[0]; // ej: '2025-05-23'
        if (!acc[dayKey]) acc[dayKey] = [];
        acc[dayKey].push(value);
        return acc;
      }, {});

      // Transformar a promedio por día y formatear para el gráfico
      const temperatureData = Object.entries(groupedByDay).map(
        ([day, values]) => {
          const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
          return {
            time: format(new Date(day), "MMM d"),
            value: parseFloat((avg / 10).toFixed(1)), // dividir por 10
          };
        }
      );

      // Ordenar por fecha
      temperatureData.sort((a, b) => new Date(a.time) - new Date(b.time));

      // Usar solo los últimos 14 días
      const last14Days = temperatureData.slice(-14);

      // Actualizar el estado de histórico
      setHistoricalData((prev) => ({
        ...prev,
        temperature: last14Days,
      }));
    } catch (error) {
      console.error("❌ Error al obtener temperaturas:", error);
    }
  };
 const fetchPulseData = async () => {
  try {
    const response = await fetch(
      "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/userInfPulse",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth_token: user.token,
        },
        body: JSON.stringify({
          userId: user.userId,
          timeStamp: 1,
        }),
      }
    );

    const result = await response.json();
    console.log("📊 Respuesta cruda de PULSE:", result);

    const rawData = result?.data?.[2];
    if (!Array.isArray(rawData)) {
      console.warn("⚠ La respuesta de PULSE no es un array válido.");
      return;
    }

    const groupedByDay = rawData.reduce((acc, [timestamp, value]) => {
      if (!timestamp || !value) return acc;
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) return acc;

      const dayKey = date.toISOString().split("T")[0];
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(value);
      return acc;
    }, {});

    const pulseData = Object.entries(groupedByDay).map(([day, values]) => {
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      return {
        time: format(new Date(day), "MMM d"),
        value: Math.round(avg),
      };
    });

    pulseData.sort((a, b) => new Date(a.time) - new Date(b.time));

    const last14Days = pulseData.slice(-14);

    setHistoricalData((prev) => ({
      ...prev,
      pulse: last14Days,
    }));
  } catch (error) {
    console.error("❌ Error al obtener PULSE:", error);
  }
};

const fetchSpo2hData = async () => {
  try {
    const response = await fetch(
      "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/userInfSpo2hValue",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth_token: user.token,
        },
        body: JSON.stringify({
          userId: user.userId,
          timeStamp: 1,
        }),
      }
    );

    const result = await response.json();
    console.log("📊 Respuesta cruda de SPO2H:", result);

    const rawData = result?.data?.[2];
    if (!Array.isArray(rawData)) {
      console.warn("⚠ La respuesta de SPO2H no es un array válido.");
      return;
    }

    const groupedByDay = rawData.reduce((acc, [timestamp, value]) => {
      if (!timestamp || !value) return acc;
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) return acc;

      const dayKey = date.toISOString().split("T")[0];
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push(value);
      return acc;
    }, {});

    const spo2hData = Object.entries(groupedByDay).map(([day, values]) => {
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      return {
        time: format(new Date(day), "MMM d"),
        value: Math.round(avg),
      };
    });

    spo2hData.sort((a, b) => new Date(a.time) - new Date(b.time));

    const last14Days = spo2hData.slice(-14);

      setHistoricalData((prev) => ({
        ...prev,
        spo2h: last14Days, // ✅ esto debe coincidir con el gráfico
      }));

  } catch (error) {
    console.error("❌ Error al obtener SPO2H:", error);
  }
};

const fetchPressureData = async () => {
  try {
    const response = await fetch(
      "https://vivebackend-a5hdfhhtbphgd4ed.canadacentral-01.azurewebsites.net/api/userInfPressure",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          auth_token: user.token,
        },
        body: JSON.stringify({
          userId: user.userId,
          timeStamp: 1,
        }),
      }
    );

    const result = await response.json();
    console.log("📊 Respuesta cruda de PRESIÓN:", result);

    const rawData = result?.data?.[2];
    if (!Array.isArray(rawData)) return;

    const groupedByDay = rawData.reduce((acc, [timestamp, high, low]) => {
      const date = new Date(timestamp * 1000);
      const dayKey = date.toISOString().split("T")[0];
      if (!acc[dayKey]) acc[dayKey] = [];
      acc[dayKey].push({ high, low });
      return acc;
    }, {});

    const pressureData = Object.entries(groupedByDay).map(([day, values]) => {
      const avgHigh = values.reduce((sum, v) => sum + v.high, 0) / values.length;
      const avgLow = values.reduce((sum, v) => sum + v.low, 0) / values.length;
      return {
        time: format(new Date(day), "MMM d"),
        bpHigh: Math.round(avgHigh),
        bpLow: Math.round(avgLow),
      };
    });

    pressureData.sort((a, b) => new Date(a.time) - new Date(b.time));
    setHistoricalData((prev) => ({ ...prev, bloodPressure: pressureData.slice(-14) }));
  } catch (error) {
    console.error("❌ Error al obtener presión arterial:", error);
  }
};

useEffect(() => {
  if (user?.userId) {
    fetchTemperatureData();
    fetchPulseData();
    fetchSpo2hData();
    fetchPressureData();
  }
}, [user?.userId]);


  const handleEditClick = (contact) => {
    console.log("📢 Editando contacto:", contact);
    setEditMode(contact.contactListId);
    setUpdatedNames((prev) => ({
      ...prev,
      [contact.contactListId]: contact.NameContact,
    }));
  };

  const handleSaveEdit = async (contact) => {
    console.log("📢 Guardando contacto:", contact);
    setLoading(true);
    try {
      await fetch(
        "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/updateContact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
          body: JSON.stringify({
            contactListId: contact.contactListId,
            PhoneNumberContact: contact.PhoneNumberContact,
            NameContact:
              updatedNames[contact.contactListId] || contact.NameContact,
            LevelContact: contact.LevelContact,
            passwordHash: "newpassword",
          }),
        }
      );

      setEditMode(null);
      setUpdatedNames({});
      fetchContacts();
    } catch (error) {
      console.error("❌ Error updating contact:", error);
      setErrorMessage(error.message);
    }
    setLoading(false);
  };

  const handleDeleteNok = async (contactListId) => {
    console.log("📢 Eliminando contacto con ID:", contactListId);
    if (!user?.token)
      return console.error("No token found. User must be logged in.");
    if (!window.confirm("Are you sure you want to delete this contact?"))
      return;

    try {
      await fetch(
        "https://vivesupportnokback-ctfcg7hfgtd6g0h2.canadacentral-01.azurewebsites.net/api/deleteContact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            auth_token: user.token,
          },
          body: JSON.stringify({ contactListId }),
        }
      );

      fetchContacts();
    } catch (error) {
      console.error("❌ Error deleting contact:", error);
    }
  };

  return (
    <div
      id="home-user"
      className="text-center"
      style={{
        backgroundColor: "#fff",
        padding: "40px",
        marginTop: "90px",
        minHeight: "100vh",
        paddingLeft: "5px",
        paddingRight: "5px",
      }}
    >
      <div className="container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <button
            onClick={() => setActiveTab("medical")}
            style={getButtonStyle(activeTab === "medical")}
          >
            Medical Data
          </button>
          <button
            onClick={() => setActiveTab("nok")}
            style={getButtonStyle(activeTab === "nok")}
          >
            Add/Delete NOK
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            style={getButtonStyle(false)}
          >
            Logout
          </button>
        </div>

        {activeTab === "medical" && (
          <>
            <div className="section-title">
              <h2>Health Monitoring</h2>
            </div>
            <div className="row">
              {healthData.map((data, index) => (
                <div
                  key={index}
                  className="col-sm-6 col-md-6 col-lg-6"
                  style={{ padding: "15px" }}
                >
                  <div style={healthCardStyle}>
                    <img
                      src={data.imageSrc}
                      alt={data.label}
                      style={imageStyle}
                    />
                    <div>
                      <h2 style={valueStyle}>{data.value}</h2>
                      <p style={labelStyle}>{data.label}</p>
                      <small style={timeStyle}>Received: {data.time}</small>
                    </div>
                  </div>
                </div>
              ))}
              <div className="container">
                <h2>Health Monitoring</h2>

                {/* Gráficos */}
                <div className="container">
                  <h3 style={{ textAlign: "center", marginTop: "20px" }}>
                    Health Trends (Last 14 Days)
                  </h3>

                  {/* Heart Rate Chart */}
<div style={{ marginBottom: "30px" }}>
  <h4>Heart Rate</h4>
  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={historicalData.pulse}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" />
      <YAxis domain={[50, 120]} />
      <Tooltip />
      <Line
        type="monotone"
        dataKey="value"
        stroke="#FF6384"
        strokeWidth={2}
      />
    </LineChart>
  </ResponsiveContainer>
</div>


                  {/* Blood Pressure Chart */}
                  <div style={{ marginBottom: "30px" }}>
  <h4>Blood Pressure</h4>
  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={historicalData.bloodPressure}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" />
      <YAxis domain={[60, 160]} />
      <Tooltip />
      <Line
        type="monotone"
        dataKey="bpHigh"
        stroke="#E74C3C"
        strokeWidth={2}
        name="Systolic (High)"
      />
      <Line
        type="monotone"
        dataKey="bpLow"
        stroke="#2ECC71"
        strokeWidth={2}
        name="Diastolic (Low)"
      />
    </LineChart>
  </ResponsiveContainer>
</div>

                  <div style={{ marginBottom: "30px" }}>
                    <h4>Temperature</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={historicalData.temperature}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis domain={[35.5, 38]} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#E67E22"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Oxygen Saturation Chart */}
                 <div style={{ marginBottom: "30px" }}>
  <h4>Oxygen Saturation (SPO2H)</h4>
  <ResponsiveContainer width="100%" height={200}>
    <LineChart data={historicalData.spo2h}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" />
      <YAxis domain={[90, 100]} />
      <Tooltip />
      <Line
        type="monotone"
        dataKey="value"
        stroke="#36A2EB"
        strokeWidth={2}
      />
    </LineChart>
  </ResponsiveContainer>
</div>

                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "nok" && (
          <div>
            <div className="section-title">
              <h2>Add/Delete NOK</h2>
            </div>

            <form onSubmit={handleAddNok} style={{ marginBottom: "20px" }}>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={newUser.name}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />
              <input
                type="text"
                name="phone"
                placeholder="Phone Number"
                value={newUser.phone}
                onChange={handleInputChange}
                required
                style={inputStyle}
              />

              <button type="submit" style={addButtonStyle} disabled={loading}>
                {loading ? "Adding..." : "Add NOK"}
              </button>
            </form>

            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

            <table style={tableStyle}>
              <thead>
                <tr style={tableHeaderStyle}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Allowed</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {nokUsers.map((contact, index) => (
                  <tr
                    key={contact.contactListId}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#f5f5f5" : "#fff",
                    }}
                  >
                    <td style={tdStyle}>
                      {editMode === contact.contactListId ? (
                        <input
                          type="text"
                          value={updatedNames[contact.contactListId] || ""}
                          onChange={(e) =>
                            setUpdatedNames((prev) => ({
                              ...prev,
                              [contact.contactListId]: e.target.value,
                            }))
                          }
                          style={{ padding: "5px", width: "100%" }}
                        />
                      ) : (
                        contact.NameContact
                      )}
                    </td>
                    <td style={tdStyle}>{contact.PhoneNumberContact}</td>
                    <td style={tdStyle}>
                      {contact.allowed === "1" ? "Yes" : "No"}
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleDeleteNok(contact.contactListId)}
                        style={deleteButtonStyle}
                      >
                        Delete
                      </button>
                      {editMode === contact.contactListId ? (
                        <button
                          onClick={() => handleSaveEdit(contact)}
                          style={saveButtonStyle}
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Save"}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditClick(contact)}
                          style={editButtonStyle}
                        >
                          ✏ Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const getButtonStyle = (isActive) => ({
  padding: "10px 20px",
  margin: "0 5px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  backgroundColor: isActive ? "#1a9766" : "#ddd",
  color: isActive ? "#fff" : "#333",
  fontSize: "16px",
  transition: "0.3s",
});

const healthCardStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  backgroundColor: "#f5f5f5",
  padding: "15px",
  borderRadius: "10px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
};

const imageStyle = {
  width: "100px",
  height: "100px",
  objectFit: "contain",
  marginRight: "10px",
};

const valueStyle = {
  margin: "0",
  fontWeight: "bold",
  color: "#333",
  fontSize: "60px",
};
const labelStyle = { margin: "0", color: "#777", fontSize: "20px" };
const timeStyle = { color: "#aaa", fontSize: "20px" };

const inputStyle = {
  padding: "10px",
  margin: "5px",
  borderRadius: "5px",
  border: "1px solid #ccc",
  width: "200px",
};

const addButtonStyle = {
  padding: "10px 20px",
  backgroundColor: "#1a9766",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const editButtonStyle = {
  backgroundColor: "#3498DB",
  color: "#fff",
  padding: "6px 12px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};
const saveButtonStyle = {
  backgroundColor: "#2ECC71",
  margin: "5px",
  color: "#fff",
  padding: "6px 12px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
};
const tableHeaderStyle = { backgroundColor: "#1a9766", color: "#fff" };
const thStyle = {
  padding: "12px",
  textAlign: "left",
  fontSize: "18px",
  fontWeight: "bold",
  borderBottom: "2px solid #ddd",
};
const tdStyle = {
  padding: "12px",
  textAlign: "left",
  fontSize: "16px",
  color: "#333",
  borderBottom: "1px solid #ddd",
};
const deleteButtonStyle = {
  backgroundColor: "#E74C3C",
  margin: "5px",
  color: "#fff",
  padding: "6px 12px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};
